import { tryOnMounted } from '@vueuse/core'

import dayjs from 'dayjs'
import { useBaseMessage } from '@/composition/useMessage'
import { useGtm } from '@/composition/useGtm'
import { usePlatformStore } from '@/store-pinia/platform'
import { GtmEventMap } from '@/view-models/gtm'
import { loadScript } from '@/utils/dom-loader'
import { FacebookDialogLogin } from '@/utils/facebook-login'
import { adaptGenderCode } from '@/utils/utils'
import utils from '@/utils'
import * as loginService from '@/service/login'
import * as memberService from '@/service/member'
import { ResponseCode } from '~/view-models/common'

/**
 * ! for component only
 */
export function useLogin() {
  const { isShowLoginStep } = storeToRefs(usePlatformStore())
  const { $store: store } = useNuxtApp()
  /**
   * * 純開 login，沒有前面 landing 畫面的步驟
   */
  function openPureLogin() {
    isShowLoginStep.value = true
    store.dispatch('dialog/setLoginDialog', { state: true })
  }

  function closeLogin() {
    isShowLoginStep.value = false
    store.dispatch('dialog/setLoginDialog', { state: false })
  }

  return {
    openPureLogin,
    closeLogin,
  }
}

export function useFBLogin() {
  const config = useRuntimeConfig()
  const { sendGtmEvent } = useGtm()

  const nuxtApp = useNuxtApp()
  const { $axios, $store: store, $i18n } = nuxtApp
  const regionId = computed(
    () => store.getters['region/region'].id || store.getters['branch/branch'].region_id || store.getters['product/productInfo'].region_id
  )

  const errorDialogPayload = reactive({
    header: $i18n.t('login_error_title'),
    message: '',
  })

  const showConsent = ref(false)
  const pendingUserInfo = ref(null)

  const { showMessageModal } = useBaseMessage({
    data: toRefs(errorDialogPayload),
    close: () => {
      errorDialogPayload.message = ''
    },
  })

  tryOnMounted(() => {
    loadScript(() => FacebookDialogLogin.init(config.public.FB_APP_ID), {
      id: 'fbLogin',
      src: 'https://connect.facebook.net/en_US/sdk.js',
      defer: true,
    })
  })

  async function login() {
    try {
      const { accessToken, userID } = await FacebookDialogLogin.login()
      const isLogin = await loginFunNow(accessToken, userID)
      if (!isLogin) {
        return false
      }
      await store.dispatch('authentication/getUserInfo')
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  /**
   *
   * @param {string} accessToken
   * @param {string} fbid
   * @returns {Promise<boolean>} Promise<boolean>
   */
  async function loginFunNow(accessToken, fbid) {
    try {
      const loginResult = await loginService.facebookLogin($axios, { fbToken: accessToken, id: fbid })
      sendTrackingCode(loginResult.data.uid, 'login')
      sendGtmEvent({
        event: GtmEventMap.ACCOUNT_LOGGED_IN,
        payload: {
          'Login Type': 'FB',
        },
      })
      return true
    } catch (error) {
      console.error('fbLogin: error', error)
      if (error.code === ResponseCode.TokenNotFound) {
        await preparePendingUserInfo(accessToken)
        return false
      }
      openErrorDialog(error.message)
      return false
    }
  }

  /**
   * Prepare pending user info and show consent modal
   * @param {string} accessToken
   * @returns {Promise<void>}
   */
  async function preparePendingUserInfo(accessToken) {
    try {
      const fbProfile = await FacebookDialogLogin.getProfile()

      pendingUserInfo.value = {
        email: fbProfile.email,
        name: fbProfile.name,
        gender: adaptGenderCode(fbProfile.gender),
        birthday: fbProfile.birthday ? dayjs(fbProfile.birthday).format('YYYY-MM-DD') : '',
        fbId: fbProfile.id,
        fbToken: accessToken,
      }

      showConsent.value = true
    } catch (error) {
      console.error('preparePendingUserInfo: error', error)
      openErrorDialog(error.message)
    }
  }

  /**
   * Handle consent confirmation from modal
   * @param {Object} userContract - { isTermsAccepted: boolean, isMarketingAllowed: boolean }
   * @returns {Promise<void>}
   */
  async function handleConsentConfirm(userContract) {
    showConsent.value = false

    if (!pendingUserInfo.value) {
      return
    }

    await registerWithConsent(userContract)
  }

  /**
   * Handle post-registration logic (region update, user storage, tracking)
   * @param {Object} registerResult
   * @returns {Promise<void>}
   */
  async function handlePostRegistration(registerResult) {
    let userInfo = registerResult.data
    if (regionId.value) {
      await memberService.updateMemberRegion($axios, regionId.value)

      userInfo = {
        ...userInfo,
        region_id: regionId.value,
      }
      if (store.getters['authentication/getUser']) {
        store.commit('authentication/UPDATE_USER_REGION_ID', regionId.value)
      }
    }
    nuxtApp.$auth.$storage.setUniversal('user', userInfo)
    store.commit('authentication/SET_USER', userInfo)
    sendTrackingCode(registerResult.data.uid, 'registration')
  }

  /**
   *
   * @param {Object} userContract - { isTermsAccepted: boolean, isMarketingAllowed: boolean }
   * @returns {Promise<boolean>}
   */
  async function registerWithConsent(userContract) {
    try {
      const registerData = {
        ...pendingUserInfo.value,
        osVersion: navigator.userAgent,
        deviceId: utils.UUID,
        userContract,
      }

      const registerResult = await loginService.register($axios, registerData, { byFacebook: true })

      if (registerResult.code !== 0) {
        throw registerResult
      }

      await handlePostRegistration(registerResult)

      pendingUserInfo.value = null
      return true
    } catch (error) {
      console.error('registerWithConsent: error', error)
      openErrorDialog(error.message)
      return false
    }
  }

  /**
   *
   * @param {string} accessToken
   * @returns {Promise<void>}
   */
  async function register(accessToken) {
    try {
      const fbProfile = await FacebookDialogLogin.getProfile()

      const registerResult = await loginService.register(
        $axios,
        {
          email: fbProfile.email,
          name: fbProfile.name,
          gender: adaptGenderCode(fbProfile.gender),
          birthday: fbProfile.birthday ? dayjs(fbProfile.birthday).format('YYYY-MM-DD') : '',
          osVersion: navigator.userAgent,
          deviceId: utils.UUID,
          fbId: fbProfile.id,
          fbToken: accessToken,
        },
        { byFacebook: true }
      )

      if (registerResult.code !== 0) {
        throw registerResult
      }

      await handlePostRegistration(registerResult)
    } catch (error) {
      console.error('fb register: error', error)
      openErrorDialog(error.message)
    }
  }

  /**
   *
   * @param {string} UID
   * @param {'registration'|'login'} trackingType
   * @returns
   */
  function sendTrackingCode(UID = '', trackingType = 'registration') {
    if (!UID) {
      return
    }
    const eventType = trackingType === 'login' ? 'login' : 'registration'
    nuxtApp.$gtm.push({
      event: eventType,
      authenticationMethod: 'facebook',
      userId: UID,
    })
  }

  /**
   *
   * @param {string} message
   */
  function openErrorDialog(message) {
    errorDialogPayload.message = message
    showMessageModal()
  }

  return {
    login,
    showConsent,
    pendingUserInfo,
    handleConsentConfirm,
  }
}
